import equal from 'fast-deep-equal/react';
import isPlainObject from 'lodash/isPlainObject';
import { runMingo, unFlatten } from '@buddy-technology/ion-helpers';
import hasCalculate from '../utils/hasCalculate';
import isRequired from '../utils/isRequired';
import flattenObj from '../utils/flattenObject';
import { useDataContext } from '../context';

/**
  @typedef SetDynamicValuesParams
 * @property  {Function} getValues - getValues function from react-hook-form
 * @property  {Function} getFieldState - getFieldState function from react-hook-form
 * @property  {Function} setValue - setValue function from react-hook-form
 * @property  {Array} flatApplication - flat array of field objects that take a value.
*/

/**
 * @name useSetDynamicValues
 * @description Returns a function to calculates any dynamic fields that haven't been directly manipulated by the user.
 * @param  {SetDynamicValuesParams} SetDynamicValuesParams all params are required.
 * @returns {Function} - setDynamicValues function
 */
const useSetDynamicValues = ({
	flatApplication = isRequired('flatApplication'),
	getValues = isRequired('getValues'),
	getFieldState = isRequired('getFieldState'),
	setValue = isRequired('setValue'),
}) => {
	const { ion: { variables }, offeringOptions } = useDataContext();
	const dataOverrides = flattenObj(offeringOptions?.data || {}, '::');

	const setDynamicValues = (event = { target: {} }) => {
		const formValues = getValues();
		const fieldsToCalculate = flatApplication.filter(({ value }) => hasCalculate(value));

		const valuesToUpdate = fieldsToCalculate.reduce((prev, current) => {
			const { id, value } = current;

			// Bail from _calculate if incoming data is anything other than undefined (we allow someone overriding with null, empty string, 0, NaN and false).
			if (typeof dataOverrides[id] !== 'undefined') {
				return prev;
			}

			const currentStateValue = formValues[id];
			const isTarget = event?.target?.name === id;
			const { isTouched } = getFieldState(id);
			// If a user has updated this field, don't update it.
			const isFocused = document.hasFocus() && document.activeElement?.name === id;

			if (isFocused || isTouched || isTarget) {
				return prev;
			}

			let { value: result } = runMingo(value._calculate, { ...unFlatten(formValues), variables, offeringOptions });

			// If runMingo returned an empty object, it's due to a calculation that couldn't find a value. Better to return an empty string to prevent [object Object] rendering in inputs.
			if (isPlainObject(result) && !Object.keys(result).length) {
				result = '';
			}

			// using fast-deep-equal to quickly compare objects, arrays, and primitives.
			if (!equal(currentStateValue, result)) {
				return { ...prev, [id]: result };
			}
			return prev;
		}, {});

		const valuesToUpdateEntries = Object.entries(valuesToUpdate);

		if (valuesToUpdateEntries.length) {
			valuesToUpdateEntries.forEach(([key, value]) => setValue(key, value));
		}
	};

	return setDynamicValues;
};

export default useSetDynamicValues;
